import { useState } from "react";
import { confirmAction } from "@/components/app/Confirm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/nav";
import { SkeletonRows } from "@/components/app/Common";
import { deleteItem, TrashButton } from "@/lib/deleteItem";

const RoleSelect = ({ value, onChange, disabled, testId }) => (
  <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="h-8 rounded-lg border border-line bg-white px-2 text-sm disabled:opacity-60" data-testid={testId}>
    {Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

function AddUserDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", role: "recruiter", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/settings/users", f);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${f.name} can now sign in`);
      setF({ name: "", email: "", role: "recruiter", password: "" });
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[6px]" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle>Add a user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-user-name-input" /></div>
          <div className="space-y-2"><Label>Work email</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-user-email-input" /></div>
          <div className="space-y-2"><Label>Role</Label><div><RoleSelect value={f.role} onChange={(role) => setF({ ...f, role })} testId="add-user-role-select" /></div></div>
          <div className="space-y-2"><Label>Temporary password</Label><Input type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="add-user-password-input" /><p className="text-xs text-ink-muted">At least 8 characters. Share it securely; they can reset it later.</p></div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="add-user-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />} Add user</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersSettings() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: () => get("/settings/users") });
  const patch = async (u, body, msg) => {
    try {
      await api.patch(`/settings/users/${u.id}`, body);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <SkeletonRows rows={4} />;
  return (
    <section className="panel overflow-hidden" data-testid="users-settings">
      <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2 border-b border-line px-6 py-4">
        <h2 className="text-lg font-semibold">Users</h2>
        <Button onClick={() => setAdding(true)} data-tour="pt-add-user-btn" data-testid="add-user-btn"><UserPlus size={16} /> Add user</Button>
      </div>
      <div className="max-lg:overflow-x-auto"><table className="w-full text-sm" data-tour="pt-users-table" data-testid="users-table">
        <thead className="bg-subtle text-left text-xs text-ink-muted">
          <tr><th className="px-6 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Role</th><th className="px-6 py-3 text-right font-medium">Active</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`}>
              <td className="px-6 py-3"><p className="font-medium text-ink">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-ink-muted">(you)</span>}</p><p className="text-xs text-ink-muted">{u.email}</p></td>
              <td className="px-4 py-3"><RoleSelect value={u.role} disabled={u.id === me.id} onChange={(role) => patch(u, { role }, `${u.name} is now ${ROLE_LABELS[role].toLowerCase()}`)} testId={`user-role-${u.email}`} /></td>
              <td className="px-6 py-3 text-right"><Switch checked={u.active} disabled={u.id === me.id} onCheckedChange={async (active) => { if (!active && !(await confirmAction({ title: "Revoke access?", body: "They'll be signed out and can't sign in until reactivated. Their history is kept.", entity: `${u.name} · ${u.email}`, confirmLabel: "Revoke access" }))) return; patch(u, { active }, `${u.name} ${active ? "reactivated" : "deactivated"}`); }} data-testid={`user-active-${u.email}`} />
                {u.id !== me.id && <TrashButton label={`Delete ${u.name}`} testId={`delete-user-${u.id}`} onClick={() => deleteItem({ title: "Delete this user?", entity: `${u.name} · ${u.email}`,
                  body: "They lose access immediately. Their notes and audit history stay, attributed to their email. To keep the account for later, turn off Active instead.", url: `/settings/users/${u.id}`,
                  success: `Deleted ${u.name}`, onDone: () => qc.invalidateQueries({ queryKey: ["users"] }) })} />}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <AddUserDialog open={adding} onOpenChange={setAdding} />
    </section>
  );
}
